import { cn } from '@commenda/utils';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

interface SlidingBorderProps {
  direction: 'horizontal' | 'vertical';
  listRef: React.RefObject<HTMLDivElement>;
}

interface TabsContextValue {
  value: string | undefined;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

interface BorderPosition {
  left: number;
  top: number;
  width: number;
  height: number;
}

const SlidingBorder = ({ direction, listRef }: SlidingBorderProps) => {
  const ctx = React.useContext(TabsContext);
  const [position, setPosition] = React.useState<BorderPosition | null>(null);
  const [isInitialMount, setIsInitialMount] = React.useState(true);

  const updatePosition = React.useCallback(() => {
    if (!listRef.current) {
      setPosition(null);
      return;
    }

    const activeTab = listRef.current.querySelector(
      '[data-state="active"]'
    ) as HTMLElement | null;

    if (!activeTab) {
      setPosition(null);
      return;
    }

    const listRect = listRef.current.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    setPosition({
      left: tabRect.left - listRect.left + listRef.current.scrollLeft,
      top: tabRect.top - listRect.top + listRef.current.scrollTop,
      width: tabRect.width,
      height: tabRect.height,
    });
  }, [listRef]);

  React.useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      updatePosition();

      if (isInitialMount) requestAnimationFrame(() => setIsInitialMount(false));
    });

    return () => cancelAnimationFrame(rafId);
  }, [ctx?.value, updatePosition, isInitialMount]);

  React.useEffect(() => {
    if (!listRef.current) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      updatePosition();
    });

    const mutationObserver = new MutationObserver(() => {
      updatePosition();
    });

    resizeObserver.observe(listRef.current);
    mutationObserver.observe(listRef.current, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-state'],
    });

    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [listRef, updatePosition]);

  if (!position) return null;

  const isHorizontal = direction === 'horizontal';

  return (
    <span
      className={cn(
        'absolute bg-fg-brand pointer-events-none',
        isInitialMount ? '' : 'transition-all duration-200 ease-out'
      )}
      style={
        isHorizontal
          ? {
              bottom: 0,
              left: position.left,
              width: position.width,
              height: 2,
            }
          : {
              right: 0,
              top: position.top,
              width: 2,
              height: position.height,
            }
      }
    />
  );
};

type TabsProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>;

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(({ value, defaultValue, onValueChange, ...props }, ref) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue);

  const currentValue = value ?? internalValue;

  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (value === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [value, onValueChange]
  );

  const contextValue = React.useMemo(
    () => ({ value: currentValue }),
    [currentValue]
  );

  return (
    <TabsContext.Provider value={contextValue}>
      <TabsPrimitive.Root
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        onValueChange={handleValueChange}
        {...props}
      />
    </TabsContext.Provider>
  );
});
Tabs.displayName = 'Tabs';

const horizontalTabsListVariants = cva('flex items-center w-full', {
  variants: {
    size: {
      sm: '',
      md: 'gap-md',
    },
    variant: {
      underline: 'border-b border-border-primary',
      border: 'border border-border-primary rounded-lg bg-bg-secondary',
      tabs: 'flex border border-border-primary rounded-lg bg-bg-primary',
      // Underline behavior with no static container border-b. Use when
      // the parent already paints its own divider.
      plain: '',
      pill: 'gap-sm w-auto',
    },
  },
  compoundVariants: [
    { variant: 'underline', size: 'sm', class: 'gap-lg' },
    { variant: 'plain', size: 'sm', class: 'gap-lg' },
    { variant: 'plain', size: 'md', class: 'gap-md' },
    { variant: 'border', size: 'sm', class: 'p-xs gap-md' },
    { variant: 'border', size: 'md', class: 'p-sm' },
    { variant: 'tabs', size: 'md', class: 'gap-md' },
  ],
});

interface HorizontalTabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof horizontalTabsListVariants> {}

function useCombinedRefs<T>(
  ...refs: (React.Ref<T> | undefined)[]
): React.RefCallback<T> {
  return React.useCallback(
    (element: T) => {
      refs.forEach((refItem) => {
        if (!refItem) return;
        if (typeof refItem === 'function') {
          refItem(element);
        } else {
          const mutableRef = refItem as React.MutableRefObject<T>;
          mutableRef.current = element;
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refs
  );
}

const HorizontalTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  HorizontalTabsListProps
>(({ className, size, variant, children, ...props }, ref) => {
  const internalRef = React.useRef<HTMLDivElement>(null);
  const combinedRef = useCombinedRefs(ref, internalRef);
  // `plain` reuses the `underline` sliding indicator — the only difference is
  // that `plain` drops the static container border. If you decouple them
  // (e.g. give `plain` its own indicator), drop it from this predicate too,
  // otherwise both variants will paint the same indicator.
  const showSlidingBorder =
    variant === 'underline' || variant === 'plain' || variant === undefined;

  return (
    <TabsPrimitive.List
      ref={combinedRef}
      className={cn(
        horizontalTabsListVariants({ size, className, variant }),
        showSlidingBorder && 'relative'
      )}
      {...props}
    >
      {children}
      {showSlidingBorder && (
        <SlidingBorder direction="horizontal" listRef={internalRef} />
      )}
    </TabsPrimitive.List>
  );
});
HorizontalTabsList.displayName = 'HorizontalTabsList';

const verticalTabsListVariants = cva('flex flex-col items-start gap-xs');

interface VerticalTabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof verticalTabsListVariants> {}

const VerticalTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  VerticalTabsListProps
>(({ className, children, ...props }, ref) => {
  const internalRef = React.useRef<HTMLDivElement>(null);
  const combinedRef = useCombinedRefs(ref, internalRef);

  return (
    <TabsPrimitive.List
      ref={combinedRef}
      className={cn(verticalTabsListVariants({ className }), 'relative')}
      {...props}
    >
      {children}
      <SlidingBorder direction="vertical" listRef={internalRef} />
    </TabsPrimitive.List>
  );
});
VerticalTabsList.displayName = 'VerticalTabsList';

const tabsTriggerVariants = cva(
  'group text-text-quaternary txt-small-medium gap-md data-[state=active]:text-text-secondary hover:text-text-secondary inline-flex items-center justify-center whitespace-nowrap disabled:pointer-events-none disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        underline: 'txt-extra-small-medium',
        vertical: 'w-full text-left',
        button:
          'focus:outline-none data-[state=active]:bg-bg-primary rounded-[8.80px] data-[state=active]:border border-border-primary txt-small-medium text-text-disabled data-[state=active]:text-text-primary hover:border',
        'button-blue':
          'focus:outline-none data-[state=active]:bg-bg-primary rounded-[8.80px] data-[state=active]:border border-border-primary txt-small-medium text-text-disabled data-[state=active]:text-fg-brand hover:border',
        tabs: 'w-full gap-0 text-text-tertiary data-[state=active]:bg-bg-primary-hover data-[state=active]:border-border-primary-hover first:rounded-l-md last:rounded-r-md data-[state=active]:text-text-primary data-[state=active]:border',
        base: 'focus:outline-none ring-0 data-[state=active]:bg-bg-secondary rounded-md txt-small-medium text-text-disabled data-[state=active]:text-text-primary hover:bg-bg-secondary',
        pill: 'focus:outline-none h-7 gap-sm rounded-full border-[0.5px] border-border-tertiary bg-bg-primary txt-extra-small-medium text-text-tertiary shadow-[0px_1px_1px_rgba(0,0,0,0.04)] hover:bg-bg-light hover:text-text-tertiary data-[state=active]:bg-bg-secondary data-[state=active]:text-text-primary',
      },
      size: {
        sm: 'text-sm',
        md: 'text-md',
      },
    },
    compoundVariants: [
      {
        variant: 'underline',
        size: 'sm',
        class: 'pt-xs pb-lg px-sm',
      },
      {
        variant: 'vertical',
        size: 'sm',
        class: 'py-md px-lg',
      },
      {
        variant: 'vertical',
        size: 'md',
        class: 'py-2.5 px-[14px]',
      },
      { variant: 'button', class: 'py-md px-lg' },
      { variant: 'button-blue', class: 'py-md px-lg' },
      { variant: 'tabs', class: 'py-md px-lg' },
      { variant: 'tabs', size: 'md', class: 'py-md px-lg' },
      { variant: 'pill', class: 'py-xs px-lg text-xs' },
    ],
    defaultVariants: {
      variant: 'underline',
      size: 'sm',
    },
  }
);

export interface TabsTriggerProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
      'onSelect'
    >,
    VariantProps<typeof tabsTriggerVariants> {
  children: React.ReactNode;
  count?: number;
  onSelect?: (event: React.SyntheticEvent<HTMLButtonElement, Event>) => void;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, size, children, count, onSelect, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant, size }), className)}
    onSelect={(event) => {
      if (onSelect) {
        onSelect(event);
      }
    }}
    {...props}
  >
    {children}
    {count !== undefined && (
      <span className="txt-extra-small-regular text-text-tertiary">
        {count}
      </span>
    )}
  </TabsPrimitive.Trigger>
));

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

interface TabsContentProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {
  /**
   * When true, the content will remain mounted in the DOM when inactive (hidden via CSS).
   * When false (default), the content returns null when inactive - no DOM element.
   */
  forceMount?: true;
}

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(({ className, value, forceMount, ...props }, ref) => {
  const ctx = React.useContext(TabsContext);

  // If we have context and forceMount is not set, return null for inactive tabs
  if (ctx && !forceMount && ctx.value !== value) {
    return null;
  }

  return (
    <TabsPrimitive.Content
      ref={ref}
      value={value}
      forceMount={forceMount}
      className={cn(
        'focus-visible:outline-none focus:outline-none',
        forceMount && 'data-[state=inactive]:hidden',
        className
      )}
      {...props}
    />
  );
});
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { HorizontalTabsList, Tabs, TabsContent, TabsTrigger, VerticalTabsList };
